import { zodResolver } from "@hookform/resolvers/zod";
import { getLocalTimeZone, type ZonedDateTime } from "@internationalized/date";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { normalizeEmail } from "@trip/utils";

import { Large } from "~/components/typography";
import { Input } from "~/components/ui/input";
import { getGoogleAuthUrl } from "~/lib/google-auth";
import { useTrack } from "~/lib/use-track";
import { usePathname } from "~/navigation";
import { trpcReact } from "~/trpc/client";
import { isTRPCClientError } from "~/trpc/utils";

import { LoadingSpinner } from "./loading-spinner";
import { SubmitButton } from "./submit-button";
import { Verify } from "./verify";

const bookMeetingSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

type BookMeetingFormData = z.infer<typeof bookMeetingSchema>;

export function BookMeetingForm({
  zonedDateTime,
}: {
  zonedDateTime: ZonedDateTime;
}) {
  const userQuery = trpcReact.auth.user.useQuery(undefined, {
    retry: false,
    throwOnError: false,
  });

  const track = useTrack();
  const t = useTranslations("BookMeeting");
  const tCommon = useTranslations("Common");
  const posthog = usePostHog();
  const pathname = usePathname();
  const mutation = trpcReact.inbound.bookConsulting.useMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<BookMeetingFormData>({
    resolver: zodResolver(bookMeetingSchema),
    defaultValues: {
      name: "",
    },
  });

  const onSubmit = async (data: BookMeetingFormData) => {
    const email = userQuery.data?.email;
    if (!email) {
      setError("root", { message: "Email is required" });
      return;
    }

    try {
      await mutation.mutateAsync({
        currentUrl: window.location.toString(),
        date: zonedDateTime.toDate(),
        email,
        name: data.name,
        timeZone: getLocalTimeZone(),
      });
      posthog.identify(normalizeEmail(email));
      posthog.setPersonProperties(undefined, {
        email,
        name: data.name,
      });
      track({ name: "Book Consultation", timeZone: getLocalTimeZone() });

      // eslint-disable-next-line react-compiler/react-compiler
      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push({
        event: "book_free_appointment",
      });
    } catch (error) {
      if (isTRPCClientError(error)) {
        if (
          error.data?.code === "BAD_REQUEST" &&
          "zodError" in error.data &&
          (error.data.zodError as { fieldErrors?: Record<string, string[]> })
            .fieldErrors
        ) {
          const fieldErrors = (
            error.data.zodError as { fieldErrors: Record<string, string[]> }
          ).fieldErrors;
          Object.entries(fieldErrors).forEach(([key, errorMessages]) => {
            if (key === "name") {
              setError("name", { message: errorMessages[0] });
            } else {
              setError("root", { message: errorMessages[0] });
            }
          });
          return;
        }
      }
      setError("root", { message: "Server error" });
    }
  };

  let state: "authorized" | "unauthorized" | "loading" =
    userQuery.isLoading === true ? "loading" : "unauthorized";

  if (userQuery.isError) {
    if (userQuery.error.data?.code === "UNAUTHORIZED") {
      state = "unauthorized";
    }
  }

  if (userQuery.data) {
    state = "authorized";
  }

  if (state === "unauthorized") {
    const url = new URL(
      window.location.origin +
        (pathname === "/" ? "/free-trip-planner" : pathname),
    );
    url.searchParams.set("slot", zonedDateTime.toString());
    const googleAuthUrl = getGoogleAuthUrl(url.pathname + url.search);
    return (
      <Verify
        googleAuthUrl={googleAuthUrl}
        onSuccess={() => {
          void userQuery.refetch();
        }}
      />
    );
  }

  return (
    <div>
      <Large className="mb-4 mt-2 block text-center">{t("details")}</Large>
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <Input placeholder={"Your name"} {...register("name")} />
        {errors.name ? (
          <p className="text-sm text-red-600">{errors.name.message}</p>
        ) : null}
        <Input
          name="email"
          type="email"
          placeholder={userQuery.data ? userQuery.data.email : "Your email"}
          readOnly
        />
        <p className="pb-2 text-xs text-neutral-500">
          {t("wantToUseAnotherEmail")}{" "}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a
            className="text-prune-500"
            href="/sign-out?next=/free-trip-planner"
          >
            {tCommon("change")}
          </a>
        </p>
        {errors.root ? (
          <p className="text-japan font-medium">{errors.root.message}</p>
        ) : null}
        {mutation.data?.status === "success" ? (
          <p className="font-medium">{t("success")}</p>
        ) : (
          <SubmitButton disabled={state === "loading" || isSubmitting}>
            {state === "loading" || isSubmitting ? (
              <LoadingSpinner />
            ) : (
              tCommon("submit")
            )}
          </SubmitButton>
        )}
      </form>
    </div>
  );
}
